import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import {
  EventNavbar,
  EventStreamHeader,
  Speakers,
  EventFooter,
  EventSponsor,
  Terms,
  ProtectedRoute,
  SEO,
  Modal,
  Button
} from 'components';
import { graphql } from 'gatsby';
import { LocalContext } from 'context';

const LivestreamTemplate = ({ data }) => {
  const { frontmatter: event } = data.markdownRemark;
  const {
    setSelectedEvent,
    theme,
    registeredRecentlyWithOneStep,
    setRegisteredRecentlyWithOneStep
  } = useContext(LocalContext);

  useEffect(() => {
    setSelectedEvent({
      eid: event.eid,
      name: event.name.replace('<br />', ' '),
      title: event.title,
      colors: event.colors,
      slug: event.slug
    });
    return () => setSelectedEvent(null);
  }, [event.name]);

  return (
    <ProtectedRoute
      eid={event.eid}
      eventSlug={event.slug}
      eventIsHidden={event.hidden}
      eventStatus={event.status}>
      <>
        <SEO
          pageSpecificTitle={`${event.title.replace('<br />', ' ')} Livestream`}
          pageSpecificDescription={event.description}
          pageSpecificThumbnail={
            event.socialThumbnail.childImageSharp.gatsbyImageData.images.fallback.src
          }
          pageSpecificThumbnailAlt={event.title.replace('<br />', ' ')}
        />
        <EventNavbar
          colors={event.colors}
          eid={event.eid}
          eventDate={event.date}
          eventSlug={event.slug}
        />
        <EventStreamHeader
          colors={event.colors}
          eid={event.eid}
          eventSlug={event.slug}
          eventDates={event.date}
          eventName={event.name}
          eventSubtitle={event.subtitle}
          eventTitle={event.title}
          eventLocation={event.location}
          eventStartTime={event.start}
          eventEndTime={event.end}
          eventDescription={event.description}
          eventBanner={event.banner}
          eventStaticImage={event.staticBannerImage}
          eventBannerVideo={event.bannerVideo}
          isParticipantsEnabled={event.isParticipantsEnabled}
          isChatEnabled={event.isChatEnabled}
          isQAndAEnabled={event.isQAndAEnabled}
          isPollsEnabled={event.isPollsEnabled}
          chatEnabled={event.chatEnabled}
          footNote={event.streamFootNote}
          endScreenImg={event.endScreenImg}
        />
        <SpeakersContainer $theme={theme} id="speakers">
          {event.speakers && <Speakers speakers={event.speakers} colors={event.colors} />}
        </SpeakersContainer>
        {/* <EventSponsor eventSponsor={event.sponsor} /> */}
        <Terms />
        <EventFooter />
        <Modal
          hideCloseButton
          openWithoutTrigger={registeredRecentlyWithOneStep}
          disableCloseOnClickOutside
          modalContent={
            <OneStepRegistered $theme={theme}>
              Your registration is confirmed.
              <br /> Thank you!
              <br />
              <Button
                type="button"
                shine
                whileHover={{ scale: 1.025 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: theme.fg,
                  width: '250px',
                  height: '3.5rem',
                  marginBottom: '0rem',
                  lineHeight: '1.2rem',
                  marginTop: '1.75rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => setRegisteredRecentlyWithOneStep(false)}>
                Continue to event
              </Button>
            </OneStepRegistered>
          }
        />
      </>
    </ProtectedRoute>
  );
};

const SpeakersContainer = styled.section`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 3rem 1rem;
  position: relative;
  top: 0;
  width: 100%;
  p {
    color: ${({ $theme }) => $theme.textColor};
  }
`;

const OneStepRegistered = styled.div`
  background: ${({ $theme }) => $theme.muted};
  border-radius: 0.625rem;
  color: white;
  display: flex;
  flex-direction: column;
  font-size: 1.75rem;
  font-style: normal;
  font-weight: bold;
  height: auto;
  justify-content: center;
  line-height: 1.275em;
  max-width: 100%;
  opacity: 0.9;
  padding: 2.5rem 1rem;
  text-align: center;
  width: 500px;
  button {
    margin: 0 auto;
  }
`;

export const query = graphql`
  query ($slug: String!) {
    markdownRemark(frontmatter: { slug: { eq: $slug } }) {
      id
      frontmatter {
        eid
        name
        slug
        status
        slug
        status
        colors {
          primary
          secondary
          tertiary
        }
        name
        title
        subtitle
        seoThumbnail {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
          }
        }
        description
        date
        location
        isParticipantsEnabled
        isChatEnabled
        isQAndAEnabled
        isPollsEnabled
        start
        end
        startTime
        endTime
        eventHighlights {
          id
          url
          thumb {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
            }
          }
          title
          description
        }
        banner {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED, quality: 90)
          }
        }
        socialThumbnail {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, height: 900, quality: 90)
          }
        }
        staticBannerImage {
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, height: 900, quality: 90)
          }
        }
        endScreenImg {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
          }
        }
        bannerVideo
        sponsor {
          name
          video
          image {
            childImageSharp {
              gatsbyImageData(layout: CONSTRAINED, height: 216, placeholder: BLURRED)
            }
          }
          description
        }
        speakers {
          id
          name
          position
          fbLink
          twLink
          linkedinLink
          emailLink
          shortSummary
          summary
          image {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
            }
          }
        }
      }
    }
  }
`;

export default LivestreamTemplate;
